import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { mapQuarterlyResultsApiToPerformanceHistorySection } from '@apis/performanceTrendsMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import QuarterlyTrendChart from '@components/charts/QuarterlyTrendChart';
import { useQuarterlyResults } from '@hooks/useQuarterlyResults';
import type {
  QuarterlyPerformanceSectionData,
} from '@app-types/performanceTrends';
import type { QuarterlyResultsForecastType } from '@app-types/performanceTrendsApi';
import { notify } from '@utils/notify';
import { getIframeQueryParamsFromSearch } from '@utils/queryParams';
import TrendsSection from './TrendsSection';
import TrendsControls from './TrendsControls';
import FinancialMatrixTable from './FinancialMatrixTable';

export interface QuarterlyPerformanceSectionProps {
  data: QuarterlyPerformanceSectionData;
}

const isQuarterlyForecastType = (
  value: string,
): value is QuarterlyResultsForecastType =>
  value === 'consensus' || value === 'company';

const initialForecastType = (value: string): QuarterlyResultsForecastType =>
  isQuarterlyForecastType(value) ? value : 'consensus';

const toDisplayPeriodCount = (value: string): number => {
  const parsed = Number(value);
  return parsed === 3 || parsed === 5 || parsed === 10 ? parsed : 3;
};

/** Quarterly performance trend section (Figma 3132:2534). */
const QuarterlyPerformanceSection: React.FC<QuarterlyPerformanceSectionProps> = ({ data }) => {
  const location = useLocation();
  const { ticker, mkt } = getIframeQueryParamsFromSearch(location.search);
  const [selectedForecast, setSelectedForecast] = useState<QuarterlyResultsForecastType>(
    initialForecastType(data.controls.selectedForecast),
  );
  const [selectedPeriod, setSelectedPeriod] = useState(data.controls.selectedPeriod);
  const displayPeriodCount = toDisplayPeriodCount(selectedPeriod);
  const quarterlyQuery = useQuarterlyResults(selectedForecast, displayPeriodCount);
  const chartContextKey = JSON.stringify([ticker, mkt, selectedForecast, displayPeriodCount]);

  useEffect(() => {
    if (quarterlyQuery.isError && shouldNotifyDataRequestError(quarterlyQuery.error)) {
      notify({
        level: 'error',
        message: getApiParamErrorNotificationMessage(quarterlyQuery.error) ??
          '四半期業績推移の取得に失敗しました。',
      });
    }
  }, [quarterlyQuery.error, quarterlyQuery.isError]);

  const quarterlyData = useMemo(
    () => mapQuarterlyResultsApiToPerformanceHistorySection(
      quarterlyQuery.data,
      data,
      { forecastType: selectedForecast, displayPeriodCount },
    ),
    [quarterlyQuery.data, data, selectedForecast, displayPeriodCount],
  );

  const handleForecastChange = (value: string): void => {
    if (isQuarterlyForecastType(value)) {
      setSelectedForecast(value);
    }
  };

  return (
    <TrendsSection
      title={quarterlyData.title}
      headerNodeId={quarterlyData.headerNodeId}
      sectionNodeId={quarterlyData.sectionNodeId}
      anchorId="quarterly"
    >
      <TrendsControls
        data={quarterlyData.controls}
        nodeId="3158:5193"
        selectedForecast={selectedForecast}
        selectedPeriod={selectedPeriod}
        onForecastChange={handleForecastChange}
        onPeriodChange={setSelectedPeriod}
      />
      <div className="trends-chart-grid">
        {quarterlyData.charts.map((chart) => (
          <div key={chart.key} className="trends-history-card">
            <div className="trends-history-card__title">{chart.title}</div>
            <QuarterlyTrendChart key={chartContextKey} data={chart} />
          </div>
        ))}
      </div>
      <FinancialMatrixTable
        data={quarterlyData.table}
        caption={quarterlyData.title}
      />
      <p className="trends-note">{quarterlyData.note}</p>
    </TrendsSection>
  );
};

export default QuarterlyPerformanceSection;
